import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '../api/client';

const CartContext = createContext(null);

// Best-effort extraction of an operator-readable error message from
// an axios failure. The backend returns Pydantic 422 detail arrays
// for invalid cart quantities and plain-string details for 400s.
function _extractCartError(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const d0 = detail[0];
    if (d0?.msg) return d0.msg;
  }
  return e?.message || 'Cart action failed';
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Surface the last cart-mutation error so pages can render a
  // graceful inline notice instead of silently failing. The previous
  // `await addItem()` style swallowed rejections at the click site,
  // leaving operators / customers with no feedback on a 422.
  const [lastError, setLastError] = useState(null);

  const loadCart = useCallback(async () => {
    try {
      const { data } = await cartApi.get();
      setItems(data.items || []);
      setCartId(data.cartId);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCart(); }, [loadCart]);

  const addItem = useCallback(async (product, quantity = 1) => {
    setLastError(null);
    try {
      const { data } = await cartApi.addItem({ productId: product._id, quantity });
      await loadCart();
      return data;
    } catch (e) {
      const msg = _extractCartError(e);
      setLastError(msg);
      // eslint-disable-next-line no-console
      console.warn('[cart] add failed:', msg);
      throw e;
    }
  }, [loadCart]);

  const updateQuantity = useCallback(async (itemId, quantity) => {
    setLastError(null);
    try {
      if (quantity <= 0) {
        await cartApi.removeItem(itemId);
      } else {
        await cartApi.updateItem(itemId, { quantity });
      }
      await loadCart();
    } catch (e) {
      const msg = _extractCartError(e);
      setLastError(msg);
      // eslint-disable-next-line no-console
      console.warn('[cart] update failed:', msg);
    }
  }, [loadCart]);

  const removeItem = useCallback(async (itemId) => {
    setLastError(null);
    try {
      await cartApi.removeItem(itemId);
      await loadCart();
    } catch (e) {
      const msg = _extractCartError(e);
      setLastError(msg);
      // eslint-disable-next-line no-console
      console.warn('[cart] remove failed:', msg);
    }
  }, [loadCart]);

  const clearCart = useCallback(() => {
    setItems([]);
    setCartId(null);
  }, []);

  const clearLastError = useCallback(() => setLastError(null), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalAmount = items.reduce((sum, i) => sum + i.priceSnapshot * i.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartId, items, loading,
      addItem, updateQuantity, removeItem, clearCart,
      totalItems, totalAmount,
      lastError, clearLastError,
      reload: loadCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
